import { useLocalStorage } from "@vueuse/core";
import { computed, onBeforeUnmount, ref, shallowRef } from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  CommunicationProvider,
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { makeRandomString } from "@/helpers/formatting";
import { useTwilio } from "@/hooks/communicationProviders";
import { useAnalyticsStore } from "@/stores/analytics";

import type { NameId } from "@/models/clients";
import type { CallLog, CallOutPayload } from "@/models/communicationLogs";
import type { Call, Device } from "@twilio/voice-sdk";
import type { ComputedRef, Ref, ShallowRef, WritableComputedRef } from "vue";

export interface ActiveCallSession extends Partial<CallLog>, CallOutPayload {
  client_name: NameId | undefined;
  partner: string | undefined;
}

export interface CallsStore {
  device: ShallowRef<Device | null>;
  deviceState: WritableComputedRef<TwilioDeviceState>;
  activeCall: ShallowRef<Call | null>;
  incomingCall: ShallowRef<Call | null>;
  callDirection: Ref<TwilioCallDirection | null>;
  isMicrophoneMuted: Ref<boolean>;
  isRingtoneSilenced: Ref<boolean>;
  isKeypadVisible: Ref<boolean>;
  activeCallSession: Ref<ActiveCallSession | null>;
  callDialog: Ref<CallLog | null>;
  isDialing: Ref<boolean>;
  isPiPOpen: WritableComputedRef<boolean>;
  isPiPSupported: Ref<boolean>;
  isCallActive: ComputedRef<boolean>;
  currentCall: ComputedRef<Call | null>;
  hasIncomingCall: ComputedRef<boolean>;
  hasOngoingCall: ComputedRef<boolean>;
  shouldDisableTwilioCall: ComputedRef<boolean>;
  showCallTimer: Ref<boolean>;
  playIncomingSound: () => void;
  stopIncomingSound: () => void;
  setIncomingCall: (call: Call) => void;
  setActiveCall: (call: Call) => void;
  clearCallState: () => void;
  setDevice: (dev: Device | null) => void;
  togglePipState: (open: boolean) => void;
  setPiPSupported: (supported: boolean) => void;
  callRequestData: Ref<CallRequestPayload | null>;
  requestCall: (session: ActiveCallSession) => void;
  notifyCallStarted: () => void;
  notifyCallEnded: () => void;
  notifyIncomingCallHandled: () => void;
  notifyCallSilenced: () => void;
  broadcastPhoneNumberUpdate: (phoneNumber: string) => void;
}

// Channels for cross-tab communication
enum COMMUNICATION_CHANNELS {
  CALL_REQUEST = "lf_twilio_call_request",
  PIP_STATE = "lf_twilio_pip_state",
}

export enum CALL_REQUEST_TYPES {
  INITIATE_CALL = "initial_call",
  CALL_STARTED = "call_started",
  CALL_ENDED = "call_ended",
  INCOMING_CALL_HANDLED = "incoming_call_handled",
  PHONE_NUMBER_UPDATE = "phone_number_update",
  CALL_SILENCED = "call_silenced",
}

export interface CallRequestPayload {
  type: CALL_REQUEST_TYPES;
  session?: ActiveCallSession;
  phoneNumber?: string;
  tabId?: string;
}

// Unique identifier for this tab to avoid processing own broadcasts
const TAB_ID = makeRandomString();

/**
 * Single source of truth for Twilio Dialer state
 * Used by both the main app and the PiP window
 */
export const useCallsStore = defineStore("calls", (): CallsStore => {
  // Device state - shallowRef because Device cannot be serialized to localStorage
  const device = shallowRef<Device | null>(null);
  const twilioStorage = useLocalStorage<{
    deviceState: TwilioDeviceState;
    isPiPOpen: boolean;
  }>("twilio", {
    deviceState: TwilioDeviceState.Idle,
    isPiPOpen: false,
  });

  // Call state
  const activeCall = shallowRef<Call | null>(null);
  const incomingCall = shallowRef<Call | null>(null);
  const callDirection = ref<TwilioCallDirection | null>(null);
  const isMicrophoneMuted = ref(false);
  const isRingtoneSilenced = ref(false);
  const isKeypadVisible = ref(false);
  const showCallTimer = ref(false);

  // Cross-tab communication for call requests using native BroadcastChannel
  const callRequestData = ref<CallRequestPayload | null>(null);
  const callChannel = ref<BroadcastChannel | null>(null);
  const pipStateChannel = ref<BroadcastChannel | null>(null);

  // Session state
  const activeCallSession = ref<ActiveCallSession | null>(null);
  const callDialog = ref<CallLog | null>(null);
  const isDialing = ref(false);

  const isPiPSupported = ref(false);

  // Audio
  const incomingAudio = shallowRef<HTMLAudioElement | null>(null);
  const audioPromise = shallowRef<Promise<void> | undefined>(undefined);

  const { preferredDialer } = useTwilio();
  const { trackEvent } = useAnalyticsStore();
  const { t } = useI18n();

  const deviceState = computed({
    get: () => twilioStorage.value.deviceState,
    set: (value: TwilioDeviceState) => {
      twilioStorage.value.deviceState = value;
    },
  });
  const isPiPOpen = computed({
    get: () => twilioStorage.value.isPiPOpen,
    set: (value: boolean) => {
      twilioStorage.value.isPiPOpen = value;
    },
  });

  const isCallActive = computed(
    () =>
      deviceState.value === TwilioDeviceState.Ongoing ||
      deviceState.value === TwilioDeviceState.Incoming
  );

  const currentCall = computed(() =>
    deviceState.value === TwilioDeviceState.Incoming
      ? incomingCall.value
      : activeCall.value
  );

  const hasIncomingCall = computed(
    () => deviceState.value === TwilioDeviceState.Incoming
  );

  const hasOngoingCall = computed(
    () => deviceState.value === TwilioDeviceState.Ongoing
  );

  const shouldDisableTwilioCall = computed(() => {
    return (
      deviceState.value !== TwilioDeviceState.Idle &&
      preferredDialer.value === CommunicationProvider.Twilio
    );
  });

  const playIncomingSound = () => {
    if (!incomingAudio.value) {
      incomingAudio.value = new Audio("/sounds/incoming.mp3");
      incomingAudio.value.loop = true;
      incomingAudio.value.volume = 1;
    }

    try {
      audioPromise.value = incomingAudio.value.play();
    } catch (error) {
      trackEvent(AnalyticsEvent.TwilioError, {
        description: TwilioCallErrors.SoundError,
        explanation: t("ERRORS.TWILIO.INCOMING_SOUND_ERROR"),
      });
      Sentry.captureException(error);
    }
  };

  const stopIncomingSound = async () => {
    await audioPromise.value;
    if (incomingAudio.value) {
      incomingAudio.value.pause();
      incomingAudio.value.currentTime = 0;
    }
  };

  const setIncomingCall = (call: Call) => {
    incomingCall.value = call;
    deviceState.value = TwilioDeviceState.Incoming;
    callDirection.value = TwilioCallDirection.Incoming;
    isRingtoneSilenced.value = false;
    playIncomingSound();
  };

  const setActiveCall = (call: Call) => {
    activeCall.value = call;
    deviceState.value = TwilioDeviceState.Ongoing;
    stopIncomingSound();
  };

  const clearCallState = () => {
    activeCall.value = null;
    incomingCall.value = null;
    deviceState.value = TwilioDeviceState.Idle;
    callDirection.value = null;
    isMicrophoneMuted.value = false;
    isRingtoneSilenced.value = false;
    isDialing.value = false;
    activeCallSession.value = null;
    isKeypadVisible.value = false;
    showCallTimer.value = false;
    stopIncomingSound();
  };

  const setDevice = (dev: Device | null) => {
    device.value = dev;
  };

  const setPiPSupported = (supported: boolean) => {
    isPiPSupported.value = supported;
  };

  // // Initialize PiP state BroadcastChannel for cross-tab sync
  if (typeof BroadcastChannel !== "undefined") {
    pipStateChannel.value = new BroadcastChannel(
      COMMUNICATION_CHANNELS.PIP_STATE
    );
    pipStateChannel.value.onmessage = (event: MessageEvent<boolean>) => {
      isPiPOpen.value = event.data;
    };
  }

  const pipStateHandler = () => {
    isPiPOpen.value = false;
    pipStateChannel.value?.postMessage(false);
  };

  const togglePipState = (isOpen: boolean) => {
    isPiPOpen.value = isOpen;
    if (isPiPOpen.value) {
      window.documentPictureInPicture?.window?.addEventListener(
        "pagehide",
        pipStateHandler
      );
      window.addEventListener("unload", pipStateHandler);
    }
    pipStateChannel.value?.postMessage(isPiPOpen.value);
  };

  // Initialize BroadcastChannel (works in Pinia store unlike VueUse composable)
  if (typeof BroadcastChannel !== "undefined") {
    callChannel.value = new BroadcastChannel(
      COMMUNICATION_CHANNELS.CALL_REQUEST
    );
    callChannel.value.onmessage = (event: MessageEvent<CallRequestPayload>) => {
      // Skip processing our own broadcasts
      if (event.data.tabId === TAB_ID) {
        return;
      }
      callRequestData.value = event.data;
      isKeypadVisible.value = false;
    };
  }

  /**
   * Request a call from any tab - PiP will handle it
   */
  const requestCall = (session: ActiveCallSession) => {
    // Must serialize to plain object - Vue Proxy objects can't be cloned by BroadcastChannel
    const plainSession = JSON.parse(JSON.stringify(session));
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.INITIATE_CALL,
      session: plainSession,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that a call has started
   */
  const notifyCallStarted = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_STARTED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that a call has ended
   */
  const notifyCallEnded = () => {
    isKeypadVisible.value = false;

    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_ENDED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that this tab is handling the incoming call
   */
  const notifyIncomingCallHandled = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.INCOMING_CALL_HANDLED,
    } as CallRequestPayload);
  };

  /**
   * Notify other tabs that the incoming call was silenced/muted
   */
  const notifyCallSilenced = () => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.CALL_SILENCED,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  /**
   * Broadcast phone number update to other tabs
   */
  const broadcastPhoneNumberUpdate = (phoneNumber: string) => {
    callChannel.value?.postMessage({
      type: CALL_REQUEST_TYPES.PHONE_NUMBER_UPDATE,
      phoneNumber,
      tabId: TAB_ID,
    } as CallRequestPayload);
  };

  onBeforeUnmount(() => {
    window.removeEventListener("unload", pipStateHandler);
  });

  return {
    device,
    deviceState,
    activeCall,
    incomingCall,
    callDirection,
    isMicrophoneMuted,
    isRingtoneSilenced,
    activeCallSession,
    callDialog,
    isDialing,
    isPiPOpen,
    isPiPSupported,
    shouldDisableTwilioCall,
    isKeypadVisible,
    showCallTimer,

    isCallActive,
    currentCall,
    hasIncomingCall,
    hasOngoingCall,

    playIncomingSound,
    stopIncomingSound,
    setIncomingCall,
    setActiveCall,
    clearCallState,
    setDevice,
    togglePipState,
    setPiPSupported,

    callRequestData,
    requestCall,
    notifyCallStarted,
    notifyCallEnded,
    notifyIncomingCallHandled,
    notifyCallSilenced,
    broadcastPhoneNumberUpdate,
  };
});
